import { AppContext, ErrorHandling, formatNumberWithComma, GeneralService, PayoutPopup, sharedStyle, SpecialBonusData, SubmissionService, SurrenderValuePaymentForAutoTerminatedPolicySubmission, TaskType, Title, TransactionType } from '@pulseops/common'
import { FieldDescription, FieldText, SectionCol, SectionContent, SectionRow } from '@pulseops/submission/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, SafeAreaView, Text, View } from 'react-native'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from '@mxt/zio'
import { useIsFocused } from '@react-navigation/native'
import { mapCashOutOption, PayoutMethod, PayoutMethodRef } from '../../payout-method'
import { PolicyServiceProps, UploadedFilesInfo } from '../policy-service-props'
import { RequestAuthenticateData } from '../../request-authen'

type SurrenderValuePaymentForAutoTerminatedPolicyProps = PolicyServiceProps<SurrenderValuePaymentForAutoTerminatedPolicySubmission.SubmitData>
export const SurrenderValuePaymentForAutoTerminatedPolicy = (props: SurrenderValuePaymentForAutoTerminatedPolicyProps) => {
  const { t } = useTranslation()
  const isFocused = useIsFocused()
  const { policyNumber, isConfirmed, officeCode, initSubmission } = props
  const [payoutData, setPayoutData] = React.useState<PayoutPopup.Summary[]>([])
  const payoutRef = React.useRef<PayoutMethodRef | null>(null)
  const { showToast } = React.useContext(AppContext.AppContextInstance)

  const SVAmount = pipe(
    ZIO.effect(() => {
      const accountGroups: SpecialBonusData.SBAccountGroups[] = [
        { sacsCode: 'LF', sacsType: 'TL' },
        { sacsCode: 'LP', sacsType: 'S' },
        { sacsCode: 'LF', sacsType: 'WF' }
      ]
      return accountGroups
    }),
    ZIO.flatMap((accountGroupInput) =>
      isFocused ? SubmissionService.getAccountBalance(props.policyNumber || '', 'TOTAL', accountGroupInput) :
        ZIO.succeed({
          body: {
            accountGroups: [{
              amount: 0,
              sacsCode: '',
              sacsType: ''
            }]
          }
        })
    ),
    ZIO.map((accountBalance) => {
      const accountAmount = accountBalance.body.accountGroups?.reduce(function (total, item) {
        return total + item.amount
      }, 0) || 0
      return accountAmount
    }),
    ErrorHandling.runDidUpdate([isFocused])
  ) || 0

  const changePayoutData = (data: PayoutPopup.Summary[]) => {
    setPayoutData([])
    if (data && data.length > 0) {
      setPayoutData(data)
      const totalPayoutAmount = data.reduce((sum, x) => sum + x.amount, 0)
      if (totalPayoutAmount !== SVAmount) {
        showToast(t('message:MS050170'), 'error')
      }
    }
  }

  const totalPayoutAmount = React.useMemo(() => {
    return payoutData.reduce((total, next) => total + Number(next.amount), 0)
  }, [payoutData])



  const getUploadedFilesInfo = () => {
    let uploadedFileList: UploadedFilesInfo[] = []
    const uploadedItem: UploadedFilesInfo = {
      uploadFiles: GeneralService.getPayoutDocuments(payoutData),
      transactionType: TransactionType.SURRENDER_VALUE_PAYMENT_FOR_AUTO_TERMINATED_POLICY,
      docTypeCode: 'DPS01',
      category: TaskType.PolicyService,
      policyNumber: policyNumber ?? '',
      officeCode: officeCode ?? ''
    }
    uploadedFileList.push(uploadedItem)
    return uploadedFileList
  }

  const getSubmitedData = (): SurrenderValuePaymentForAutoTerminatedPolicySubmission.SubmitData => {
    return {
      policy: {
        policyNo: policyNumber ?? ''
      },
      attributesExt: {
        AUTO_TERMINATED_POLICY_AMOUNT: SVAmount,
        TOTAL_PAYOUT_AMOUNT: totalPayoutAmount
      },
      cashOutOptions: mapCashOutOption(payoutData)
    }
  }

  const isValidSubmitedData = () => {
    if (totalPayoutAmount <= 0) {
      showToast(t('message:MS050241'), 'error')
      return false
    } else if (totalPayoutAmount !== SVAmount) {
      showToast(t('message:MS050170'), 'error')
      return false
    } else {
      return true
    }
  }


  initSubmission({
    validate: async (isContinue) => {
      if (isValidSubmitedData()) {
        return {
          url: (policyNum: string) => `wf-api/policy/${policyNum}/auto-terminated-policy`,
          body: getSubmitedData(),
          transaction: TransactionType.SURRENDER_VALUE_PAYMENT_FOR_AUTO_TERMINATED_POLICY,
          transactionName: RequestAuthenticateData.TransactionLabelShort(TransactionType.SURRENDER_VALUE_PAYMENT_FOR_AUTO_TERMINATED_POLICY),
          collerationId: policyNumber ?? '',
          uploadedFilesInfo: getUploadedFilesInfo()
        }
      } else {
        return false
      }
    },
    clear: () => {
      payoutRef.current?.clearData()
    }
  })
  return (
    <SafeAreaView style={sharedStyle.container}>
      <Title title={t('AutoTerminatedPolicy:VALUEPAYMENTFORAUTOTERMINATEDPOLICY')} wrapperStyle={sharedStyle.secondLine} />
      <SectionContent>
        <SectionRow>
          <SectionCol>
            <FieldText text={t('AutoTerminatedPolicy:SVAmount')}></FieldText>
            <FieldDescription text={formatNumberWithComma(SVAmount)} isCurrencyUnit
            ></FieldDescription>
          </SectionCol>
        </SectionRow>
      </SectionContent>
      <PayoutMethod
        transactionType={TransactionType.SURRENDER_VALUE_PAYMENT_FOR_AUTO_TERMINATED_POLICY}
        value={payoutData}
        policyNum={policyNumber ?? ''}
        methods={[
          PayoutPopup.PayoutMethods.CASHLESS,
          PayoutPopup.PayoutMethods.CASH_AT_COUNTER,
          PayoutPopup.PayoutMethods.PAID_AT_BANK,
          PayoutPopup.PayoutMethods.BANKTRANSFER,
        ]}
        maxAmount={SVAmount}
        onChange={changePayoutData}
        editable={!isConfirmed}
        ref={payoutRef}
        officeCode={officeCode}
      />
    </SafeAreaView>
  )
}